import React, { useState, useEffect } from 'react';
import { Send, MessageSquare, Users, Clock, AlertCircle, Plus, Search, Filter, Eye, Trash2, Edit } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import toast from 'react-hot-toast';

const MessagesPage = () => {
  const { company, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [stats, setStats] = useState({ total: 0, unread: 0 });
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showViewMessage, setShowViewMessage] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);

  useEffect(() => {
    if (company?.id) {
      loadMessages();
      loadStats();
    }
  }, [company]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await dataHelpers.getMessages(company.id);
      setMessages(data);
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Erro ao carregar mensagens');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await dataHelpers.getMessageStats(company.id);
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }  };

  const handleViewMessage = (message) => {
    setSelectedMessage(message);
    setShowViewMessage(true);
  };

  const handleDeleteClick = (message) => {
    setMessageToDelete(message);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!messageToDelete) return;

    try {
      await dataHelpers.deleteMessage(messageToDelete.id);
      toast.success('Mensagem excluída com sucesso!');
      loadMessages();
      loadStats();
      setShowDeleteDialog(false);
      setMessageToDelete(null);
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Erro ao excluir mensagem');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
    setMessageToDelete(null);
  };

  const filteredMessages = messages.filter(message => {
    const matchesSearch = message.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         message.content.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterType === 'all') return matchesSearch;
    return matchesSearch && message.message_type === filterType;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'normal': return 'text-blue-600 bg-blue-100';
      case 'low': return 'text-gray-600 bg-gray-100';
      default: return 'text-blue-600 bg-blue-100';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'announcement': return 'text-purple-600 bg-purple-100';
      case 'individual': return 'text-green-600 bg-green-100';
      case 'general': return 'text-blue-600 bg-blue-100';
      default: return 'text-blue-600 bg-blue-100';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'announcement': return 'Comunicado';
      case 'individual': return 'Individual';
      case 'general': return 'Geral';
      default: return 'Geral';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'urgent': return 'Urgente';
      case 'high': return 'Alta';
      case 'normal': return 'Normal';
      case 'low': return 'Baixa';
      default: return 'Normal';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Mensagens</h1>
          <p className="text-gray-600 mt-1">Comunicação com pais e responsáveis</p>
        </div>        <Button
          onClick={() => setShowNewMessage(true)}
          className="bg-blue-600 hover:bg-blue-700"
          disabled={!user}
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova Mensagem
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6">
          <div className="flex items-center">
            <MessageSquare className="w-8 h-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Mensagens</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <Users className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Mensagens Lidas</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total - stats.unread}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <AlertCircle className="w-8 h-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Não Lidas</p>
              <p className="text-2xl font-bold text-gray-900">{stats.unread}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Buscar mensagens..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              icon={Search}
            />
          </div>
          <div className="sm:w-48">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Todos os tipos</option>
              <option value="general">Geral</option>
              <option value="individual">Individual</option>
              <option value="announcement">Comunicado</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Messages List */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Título
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prioridade
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Destinatários
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredMessages.map((message) => (
                <tr key={message.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {message.title}
                      </div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {message.content}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(message.message_type)}`}>
                      {getTypeLabel(message.message_type)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(message.priority)}`}>
                      {getPriorityLabel(message.priority)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {message.recipients?.length || 0} destinatários
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDate(message.created_at)}
                  </td>                  <td className="px-6 py-4 text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleViewMessage(message)}
                      className="text-blue-600 hover:text-blue-900 transition-colors"
                      title="Visualizar"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(message)}
                      className="text-red-600 hover:text-red-900 transition-colors"
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredMessages.length === 0 && (
          <div className="text-center py-8">
            <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Nenhuma mensagem encontrada</p>
          </div>
        )}
      </Card>      {/* New Message Modal */}      {showNewMessage && user && (
        <NewMessageModal
          companyId={company.id}
          senderId={user.id}
          senderName={user.name || user.email?.split('@')[0] || 'Sistema'}
          onClose={() => setShowNewMessage(false)}
          onSuccess={() => {
            setShowNewMessage(false);
            loadMessages();
            loadStats();
          }}
        />
      )}

      {/* Modal de Visualização de Mensagem */}
      {showViewMessage && selectedMessage && (
        <ViewMessageModal
          message={selectedMessage}
          onClose={() => {
            setShowViewMessage(false);
            setSelectedMessage(null);
          }}
        />
      )}

      {/* Dialog de Confirmação de Exclusão */}
      {showDeleteDialog && messageToDelete && (
        <DeleteConfirmDialog
          message={messageToDelete}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
      )}
    </div>
  );
};

// Modal para nova mensagem
const NewMessageModal = ({ companyId, senderId, senderName, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    message_type: 'general',
    priority: 'normal',
    target_audience: 'all',
    class_filter: '',
    recipients: []
  });
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [parents, setParents] = useState([]);

  useEffect(() => {
    loadStudentsAndParents();
  }, []);

  const loadStudentsAndParents = async () => {
    try {
      const [studentsData, parentsData] = await Promise.all([
        dataHelpers.getStudents(companyId),
        dataHelpers.getUsers(companyId)
      ]);
      setStudents(studentsData);
      setParents(parentsData.filter(user => user.type === 'parent'));
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Título e conteúdo são obrigatórios');
      return;
    }    try {
      setLoading(true);
      await dataHelpers.createMessage(companyId, {
        ...formData,
        sender_id: senderId,
        sender_name: senderName
      });
      
      toast.success('Mensagem enviada com sucesso!');
      onSuccess();
    } catch (error) {
      console.error('Error creating message:', error);
      toast.error('Erro ao enviar mensagem');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const uniqueClasses = [...new Set(students.map(s => s.class_name).filter(Boolean))];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Nova Mensagem</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Título *
              </label>
              <Input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Título da mensagem"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Conteúdo *
              </label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Digite sua mensagem..."
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo
                </label>
                <select
                  name="message_type"
                  value={formData.message_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="general">Geral</option>
                  <option value="individual">Individual</option>
                  <option value="announcement">Comunicado</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prioridade
                </label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="low">Baixa</option>
                  <option value="normal">Normal</option>
                  <option value="high">Alta</option>
                  <option value="urgent">Urgente</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Destinatários
              </label>
              <select
                name="target_audience"
                value={formData.target_audience}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Todos os pais</option>
                <option value="class">Turma específica</option>
                <option value="specific">Pais específicos</option>
              </select>
            </div>

            {formData.target_audience === 'class' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Turma
                </label>
                <select
                  name="class_filter"
                  value={formData.class_filter}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecione uma turma</option>
                  {uniqueClasses.map(className => (
                    <option key={className} value={className}>
                      {className}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? 'Enviando...' : 'Enviar Mensagem'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>  );
};

// Modal para visualização de mensagem
const ViewMessageModal = ({ message, onClose }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'normal': return 'bg-blue-100 text-blue-800';
      case 'low': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'announcement': return 'bg-blue-100 text-blue-800';
      case 'general': return 'bg-green-100 text-green-800';
      default: return 'bg-green-100 text-green-800';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high': return 'Alta';
      case 'normal': return 'Normal';
      case 'low': return 'Baixa';
      default: return 'Normal';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'urgent': return 'Urgente';
      case 'announcement': return 'Comunicado';
      case 'general': return 'Geral';
      default: return 'Geral';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Visualizar Mensagem</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Informações da mensagem */}
          <div className="space-y-4">
            {/* Título */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Título</label>
              <h3 className="text-lg font-semibold text-gray-900">{message.title}</h3>
            </div>

            {/* Metadados */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(message.message_type)}`}>
                  {getTypeLabel(message.message_type)}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prioridade</label>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(message.priority)}`}>
                  {getPriorityLabel(message.priority)}
                </span>
              </div>
            </div>

            {/* Remetente e Data */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remetente</label>
                <p className="text-sm text-gray-900">{message.sender_name || 'Sistema'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data de Envio</label>
                <p className="text-sm text-gray-900">{formatDate(message.created_at)}</p>
              </div>
            </div>

            {/* Destinatários */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Destinatários</label>
              <p className="text-sm text-gray-900">
                {message.target_audience === 'all' && 'Todos os pais'}
                {message.target_audience === 'class' && `Turma: ${message.class_filter}`}
                {message.target_audience === 'specific' && `${message.recipients?.length || 0} destinatários específicos`}
              </p>
            </div>

            {/* Conteúdo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Conteúdo</label>
              <div className="bg-gray-50 rounded-lg p-4 border">
                <p className="text-gray-900 whitespace-pre-wrap">{message.content}</p>
              </div>
            </div>

            {/* Anexo (se houver) */}
            {message.attachment_url && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Anexo</label>
                <a
                  href={message.attachment_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  {message.attachment_name || 'Ver anexo'}
                </a>
              </div>
            )}

            {/* Estatísticas */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">Estatísticas</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Total de destinatários:</span>
                  <span className="ml-2 font-medium">{message.recipients?.length || 0}</span>
                </div>
                <div>
                  <span className="text-gray-600">Mensagens lidas:</span>
                  <span className="ml-2 font-medium">
                    {message.recipients?.filter(r => r.read_at).length || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end mt-6">
            <Button
              onClick={onClose}
              variant="secondary"
            >
              Fechar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Dialog de confirmação para exclusão
const DeleteConfirmDialog = ({ message, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center mb-4">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <Trash2 className="h-6 w-6 text-red-600" />
            </div>
          </div>

          {/* Content */}
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Excluir Mensagem
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Tem certeza que deseja excluir a mensagem <strong>"{message.title}"</strong>?
              Esta ação não pode ser desfeita.
            </p>
            
            {/* Informações da mensagem */}
            <div className="bg-gray-50 rounded-lg p-3 text-left text-sm mb-4">
              <div className="flex justify-between items-center mb-1">
                <span className="text-gray-600">Destinatários:</span>
                <span className="font-medium">{message.recipients?.length || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Data:</span>
                <span className="font-medium">
                  {new Date(message.created_at).toLocaleDateString('pt-BR')}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3">
            <Button
              onClick={onCancel}
              variant="secondary"
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={onConfirm}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              Excluir
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
